import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Play, CheckCircle, Lock, FileText, Video,
  ChevronDown, ChevronRight, Clock, BookOpen, ClipboardList, Award, AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AcademyCurso = () => {

  const { cursoId } = useParams();
  const navigate = useNavigate();
  const [curso, setCurso] = useState(null);
  const [modulos, setModulos] = useState([]);
  const [progresso, setProgresso] = useState(null);
  const [expandedModulos, setExpandedModulos] = useState({});
  const [statusQuestionarios, setStatusQuestionarios] = useState({});
  const [loading, setLoading] = useState(true);
  
  const token = localStorage.getItem('tayane_token');

  useEffect(() => {
    fetchCurso();
  }, [cursoId]);

  const fetchCurso = async () => {
    try {
      const [cursoRes, modulosRes, progressoRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/academy/cursos/${cursoId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/academy/cursos/${cursoId}/modulos`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/academy/cursos/${cursoId}/progresso`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCurso(cursoRes.data);
      
      // Carregar aulas e questionários de cada módulo
      const modulosComAulas = await Promise.all(
        (Array.isArray(modulosRes.data) ? modulosRes.data : (modulosRes.data?.modulos || modulosRes.data?.items || [])).map(async (modulo) => {
          const [aulasRes, questionariosRes] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/academy/modulos/${modulo.id}/aulas`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${BACKEND_URL}/api/academy/modulos/${modulo.id}/questionarios`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          ]);
          return { 
            ...modulo, 
            aulas: aulasRes.data,
            questionarios: questionariosRes.data
          };
        })
      );
      
      // Buscar status dos questionários de cada módulo
      const statusPromises = (Array.isArray(modulosRes.data) ? modulosRes.data : (modulosRes.data?.modulos || modulosRes.data?.items || [])).map(async (modulo) => {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/academy/modulos/${modulo.id}/status-questionarios`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return { moduloId: modulo.id, status: res.data };
        } catch {
          return { moduloId: modulo.id, status: null };
        }
      });
      
      const statusResults = await Promise.all(statusPromises);
      const statusMap = {};
      statusResults.forEach(({ moduloId, status }) => {
        statusMap[moduloId] = status;
      });
      setStatusQuestionarios(statusMap);
      
      setModulos(modulosComAulas);
      setProgresso(progressoRes.data);
      
      // Expandir primeiro módulo por padrão
      if (modulosComAulas.length > 0) {
        setExpandedModulos({ [modulosComAulas[0].id]: true });
      }
    } catch (error) {
      console.error("Erro ao carregar curso:", error);
      toast.error("Erro ao carregar curso");
      navigate("/crm/academy");
    } finally {
      setLoading(false);
    }
  };

  const getModuloProgresso = (moduloId) => {
    if (!progresso?.modulos_progresso) return { percentual: 0, aulas_concluidas: 0 };
    return progresso.modulos_progresso.find(m => m.modulo_id === moduloId) || { percentual: 0, aulas_concluidas: 0 };
  };

  const toggleModulo = (moduloId) => {
    setExpandedModulos(prev => ({ ...prev, [moduloId]: !prev[moduloId] }));
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'video': return <Video className="w-4 h-4 text-blue-500" />;
      case 'pdf': return <FileText className="w-4 h-4 text-orange-500" />;
      default: return <FileText className="w-4 h-4 text-green-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!curso) return null;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
      
      <div className="flex-1 p-4 lg:p-8 overflow-auto pt-20 lg:pt-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/crm/academy")}
              className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                {curso.titulo}
              </h1>
              {curso.descricao && (
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  {curso.descricao}
                </p>
              )}
            </div>
          </div>

          {/* Progresso Geral */}
          <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Progresso do Curso
              </span>
              <span className="text-sm font-bold text-primary">
                {progresso?.percentual || 0}%
              </span>
            </div>
            <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${progresso?.percentual || 0}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-slate-500 dark:text-slate-400">
              <span>{progresso?.aulas_concluidas || 0} de {progresso?.total_aulas || 0} aulas concluídas</span>
              <span>{modulos.length} módulos</span>
            </div>
          </div>

          {/* Módulos */}
          <div className="space-y-3">
            {modulos.map((modulo, index) => {
              const moduloProg = getModuloProgresso(modulo.id);
              const isExpanded = expandedModulos[modulo.id];
              const moduloStatus = statusQuestionarios[modulo.id];
              const temQuestionarios = modulo.questionarios?.length > 0;
              const todosAprovados = moduloStatus?.todos_aprovados;
              
              return (
                <div 
                  key={modulo.id}
                  className="glass-panel dark:bg-slate-900/80 rounded-xl overflow-hidden"
                >
                  {/* Header do Módulo */}
                  <button
                    onClick={() => toggleModulo(modulo.id)}
                    className="w-full p-4 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      moduloProg.percentual === 100 && (!temQuestionarios || todosAprovados)
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600' 
                        : 'bg-primary/10 text-primary'
                    }`}>
                      {moduloProg.percentual === 100 && (!temQuestionarios || todosAprovados) ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <span className="font-bold">{index + 1}</span>
                      )}
                    </div>
                    
                    <div className="flex-1 text-left">
                      <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                        {modulo.titulo}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Play className="w-3 h-3" />
                          {modulo.total_aulas} aulas
                        </span>
                        {temQuestionarios && (
                          <span className="flex items-center gap-1 text-purple-600 dark:text-purple-400">
                            <ClipboardList className="w-3 h-3" />
                            {modulo.questionarios.length} questionário{modulo.questionarios.length > 1 ? 's' : ''}
                          </span>
                        )}
                        <span>{moduloProg.percentual}% concluído</span>
                      </div>
                    </div>
                    
                    {/* Badges de status */}
                    <div className="flex items-center gap-2">
                      {temQuestionarios && (
                        todosAprovados ? (
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium flex items-center gap-1">
                            <Award className="w-3 h-3" />
                            Aprovado
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-medium flex items-center gap-1">
                            <ClipboardList className="w-3 h-3" />
                            Avaliação
                          </span>
                        )
                      )}
                    </div>
                    
                    <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    </div>
                  </button>
                  
                  {/* Conteúdo Expandido */}
                  {isExpanded && (
                    <div className="border-t border-slate-200 dark:border-slate-700">
                      {/* Aulas */}
                      {modulo.aulas && modulo.aulas.length > 0 ? (
                        <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                          {modulo.aulas.map((aula, aulaIndex) => (
                            <button
                              key={aula.id}
                              onClick={() => navigate(`/crm/academy/aula/${aula.id}`)}
                              className="w-full p-4 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                            >
                              <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 text-xs font-medium text-slate-600 dark:text-slate-400">
                                {aulaIndex + 1}
                              </div>
                              
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {getTipoIcon(aula.tipo)}
                              </div>
                              
                              <div className="flex-1 text-left">
                                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200">
                                  {aula.titulo}
                                </h4>
                                {aula.duracao_minutos && (
                                  <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                                    <Clock className="w-3 h-3" />
                                    {aula.duracao_minutos} min
                                  </span>
                                )}
                              </div>
                              
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 text-center text-sm text-slate-500 dark:text-slate-400">
                          Nenhuma aula neste módulo ainda
                        </div>
                      )}
                      
                      {/* Questionários */}
                      {temQuestionarios && (
                        <div className="border-t border-slate-200 dark:border-slate-700 p-4 bg-purple-50/50 dark:bg-purple-900/5">
                          <div className="flex items-center gap-2 mb-3">
                            <ClipboardList className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                              Avaliação do Módulo
                            </span>
                          </div>
                          
                          <div className="space-y-2">
                            {modulo.questionarios.map((q) => {
                              const qStatus = moduloStatus?.questionarios?.find(s => s.questionario_id === q.id);
                              const aprovado = qStatus?.aprovado;
                              const tentativas = qStatus?.tentativas || 0;
                              const tentativasPermitidas = qStatus?.tentativas_permitidas || q.tentativas_permitidas;
                              const podeTentar = tentativasPermitidas === 0 || tentativas < tentativasPermitidas;
                              
                              return (
                                <div 
                                  key={q.id}
                                  className={`p-3 rounded-lg flex items-center justify-between ${
                                    aprovado
                                      ? 'bg-green-100 dark:bg-green-900/20'
                                      : 'bg-white dark:bg-slate-800'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    {aprovado ? (
                                      <Award className="w-5 h-5 text-green-600 dark:text-green-400" />
                                    ) : (
                                      <ClipboardList className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                    )}
                                    <div>
                                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                                        {q.titulo}
                                      </span>
                                      <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                        <span>{q.total_questoes} questões</span>
                                        <span>•</span>
                                        <span>Mínimo {q.nota_minima}%</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {aprovado ? (
                                    <span className="text-sm text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                                      <CheckCircle className="w-4 h-4" />
                                      Aprovado
                                    </span>
                                  ) : podeTentar ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/crm/academy/questionario/${q.id}`);
                                      }}
                                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                                      data-testid={`fazer-questionario-${q.id}`}
                                    >
                                      {tentativas > 0 ? 'Tentar Novamente' : 'Fazer Avaliação'}
                                    </button>
                                  ) : (
                                    <span className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                                      <Lock className="w-4 h-4" />
                                      Bloqueado
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {modulos.length === 0 && (
            <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-8 text-center">
              <BookOpen className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400">
                Este curso ainda não possui módulos
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcademyCurso;
